// Meeting Create/Edit form fields. The surface:
// drops the dead Frequency / Minutes-link / Actions inputs, swaps the
// `+ Add attendee` UI for the ChipInput primitive, gates Date behind
// the Recurring toggle (when on, an inline recurrence panel takes its
// place with Frequency / Interval / Start / End inputs), and folds
// Evidence links + Notes behind a collapsible Advanced toggle. The
// component also publishes a primary-button copy hint to the parent
// via `onPrimaryCopyChange` so the modal's Save action reads
// "Create meeting" for one-offs and "Create N occurrences" once the
// recurrence rule is complete enough to count.

import { type ChangeEvent, useEffect, useId, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import RecurrencePreviewLine from "../deliverable/RecurrencePreviewLine";
import { countRecurrenceOccurrences } from "../deliverable/recurrenceCount";
import ChipInput from "../shared/ChipInput";
import RecurrenceIntervalInput from "../shared/RecurrenceIntervalInput";
import { frequencyUnitLabel } from "../shared/frequencyUnitLabel";
import { useLookups } from "../../../../hooks/useLookups";
import type { RecurrenceFrequency, RecurrenceRule } from "../../../../types";
import type { MeetingFieldErrors, MeetingFormState } from "./meetingFormState";

const FREQUENCIES: RecurrenceFrequency[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annually",
];

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultRule(): RecurrenceRule {
  return { frequency: "monthly", interval: 1, startDate: todayIsoDate() };
}

export interface MeetingFormFieldsProps {
  value: MeetingFormState;
  onChange: (patch: Partial<MeetingFormState>) => void;
  errors?: MeetingFieldErrors;
  disabled?: boolean;
  idPrefix: string;
  /** Optional — parent observes the primary-action copy that the form
   *  recommends ("Create meeting" / "Create N occurrences"). */
  onPrimaryCopyChange?: (copy: string) => void;
  /** Effective end date of the parent contract (admin-maintained). Passed
   *  down for Task 7's end-date helper text and serialiser auto-fill. */
  contractEndDate?: string;
}

function derivePrimaryCopy(state: MeetingFormState): string {
  if (!state.isRecurring || !state.recurrenceRule) return "Create meeting";
  const rule = state.recurrenceRule;
  if (!rule.startDate || !rule.endDate) return "Create meeting";
  if (rule.endDate < rule.startDate) return "Create meeting";
  const count = countRecurrenceOccurrences(rule);
  if (count <= 0) return "Create meeting";
  return `Create ${count} occurrences`;
}

export default function MeetingFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  idPrefix,
  onPrimaryCopyChange,
  contractEndDate,
}: MeetingFormFieldsProps) {
  const recurrenceId = useId();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const patch =
    <K extends keyof MeetingFormState>(field: K) =>
    (
      e: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) =>
      onChange({ [field]: e.target.value } as Pick<MeetingFormState, K>);

  const { data: lookupData } = useLookups("meeting-type");
  const meetingTypes = (lookupData?.items ?? [])
    .filter((l) => l.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Publish the recommended primary-button copy whenever the recurrence
  // shape changes so the parent modal's Save button stays in lockstep.
  useEffect(() => {
    if (!onPrimaryCopyChange) return;
    onPrimaryCopyChange(derivePrimaryCopy(value));
  }, [onPrimaryCopyChange, value]);

  const handleToggleRecurring = () => {
    if (value.isRecurring) {
      onChange({ isRecurring: false });
      return;
    }
    onChange({
      isRecurring: true,
      recurrenceRule: value.recurrenceRule ?? defaultRule(),
    });
  };

  const patchRule = (update: Partial<RecurrenceRule>) => {
    const current = value.recurrenceRule ?? defaultRule();
    onChange({ recurrenceRule: { ...current, ...update } });
  };

  const attendeeHelper = value.isRecurring
    ? "Default attendees applied to every occurrence — editable per occurrence later."
    : "Press Enter or comma to add. Free-text — no need to match a system user.";

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="label">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          className="input"
          value={value.name}
          onChange={patch("name")}
          disabled={disabled}
          maxLength={200}
          aria-invalid={!!errors.name}
        />
        {errors.name && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-type`} className="label">
            Meeting type <span className="text-red-600">*</span>
          </label>
          {meetingTypes.length === 0 ? (
            <>
              <select
                id={`${idPrefix}-type`}
                className="input"
                value=""
                disabled
              >
                <option value="">— No types configured —</option>
              </select>
              <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                Add meeting types in Admin → Lookups.
              </p>
            </>
          ) : (
            <select
              id={`${idPrefix}-type`}
              className="input"
              value={value.meetingType}
              onChange={patch("meetingType")}
              disabled={disabled}
              aria-invalid={!!errors.meetingType}
            >
              <option value="">— Select a type —</option>
              {meetingTypes.map((t) => (
                <option key={t.itemId} value={t.itemId}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
          {errors.meetingType && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.meetingType}
            </p>
          )}
        </div>
        {!value.isRecurring && (
          <div>
            <label htmlFor={`${idPrefix}-date`} className="label">
              Date <span className="text-red-600">*</span>
            </label>
            <input
              id={`${idPrefix}-date`}
              type="date"
              className="input"
              value={value.date}
              onChange={patch("date")}
              disabled={disabled}
              aria-invalid={!!errors.date}
            />
            {errors.date && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.date}
              </p>
            )}
          </div>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-contract-reference`} className="label">
          Contract reference
        </label>
        <input
          id={`${idPrefix}-contract-reference`}
          className="input"
          value={value.contractReference}
          onChange={patch("contractReference")}
          disabled={disabled}
          maxLength={100}
          placeholder="e.g. Clause 8.4"
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="label">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input min-h-[60px]"
          value={value.description}
          onChange={patch("description")}
          disabled={disabled}
          maxLength={500}
        />
      </div>

      <div>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={value.isRecurring}
            onChange={handleToggleRecurring}
            disabled={disabled}
            className="accent-brand-600"
          />
          <span className="text-caption font-medium text-slate-700 dark:text-slate-300">
            Recurring meeting
          </span>
        </label>
        {value.isRecurring && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <span className="text-caption text-slate-700 dark:text-slate-300">
                  Repeat every
                </span>
                <div>
                  <label
                    htmlFor={`${recurrenceId}-interval`}
                    className="sr-only"
                  >
                    Repeat every
                  </label>
                  <RecurrenceIntervalInput
                    id={`${recurrenceId}-interval`}
                    value={value.recurrenceRule?.interval ?? 1}
                    onCommit={(interval) => patchRule({ interval })}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label htmlFor={`${recurrenceId}-unit`} className="sr-only">
                    Unit
                  </label>
                  <select
                    id={`${recurrenceId}-unit`}
                    className="input"
                    value={value.recurrenceRule?.frequency ?? "monthly"}
                    onChange={(e) =>
                      patchRule({
                        frequency: e.target.value as RecurrenceFrequency,
                      })
                    }
                    disabled={disabled}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>
                        {frequencyUnitLabel(
                          f,
                          value.recurrenceRule?.interval ?? 1,
                        )}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label htmlFor={`${recurrenceId}-start`} className="label">
                    Start date
                  </label>
                  <input
                    id={`${recurrenceId}-start`}
                    type="date"
                    className="input"
                    value={value.recurrenceRule?.startDate ?? ""}
                    onChange={(e) => patchRule({ startDate: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label htmlFor={`${recurrenceId}-end`} className="label">
                    End date
                  </label>
                  <input
                    id={`${recurrenceId}-end`}
                    type="date"
                    className="input"
                    value={value.recurrenceRule?.endDate ?? ""}
                    onChange={(e) =>
                      patchRule({ endDate: e.target.value || undefined })
                    }
                    disabled={disabled}
                  />
                  {!value.recurrenceRule?.endDate && (
                    <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                      {contractEndDate
                        ? (() => {
                            const [y, m, d] = contractEndDate.split("-").map(Number);
                            return `If blank, defaults to contract end date: ${d}/${m}/${y}.`;
                          })()
                        : "If omitted, generation caps at 104 occurrences."}
                    </p>
                  )}
                </div>
              </div>
              {value.recurrenceRule && (
                <div className="mt-2">
                  <RecurrencePreviewLine
                    frequency={value.recurrenceRule.frequency}
                    interval={value.recurrenceRule.interval ?? 1}
                    startDate={value.recurrenceRule.startDate ?? ""}
                    endDate={value.recurrenceRule.endDate ?? ""}
                  />
                </div>
              )}
              {errors.recurrenceStartDate && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.recurrenceStartDate}
                </p>
              )}
              {errors.recurrenceEndDate && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.recurrenceEndDate}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <span className="label">Attendees</span>
        <ChipInput
          values={value.attendees}
          onChange={(attendees) => onChange({ attendees })}
          placeholder="Add an attendee"
          inputAriaLabel="Add an attendee"
          helperText={attendeeHelper}
          disabled={disabled}
        />
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          aria-expanded={advancedOpen}
          className="inline-flex items-center gap-1.5 text-caption font-medium text-slate-600 hover:text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:text-slate-100"
        >
          {advancedOpen ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          Advanced
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-4">
            <EvidenceLinksField
              value={value.evidenceLinks}
              onChange={(v) => onChange({ evidenceLinks: v })}
              disabled={disabled}
            />
            <div>
              <label htmlFor={`${idPrefix}-notes`} className="label">
                Notes
              </label>
              <textarea
                id={`${idPrefix}-notes`}
                className="input min-h-[80px]"
                value={value.notes}
                onChange={patch("notes")}
                disabled={disabled}
                maxLength={2000}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
